import StateNotification from "@vfuk/core-state-notification";
import Paragraph from "@vfuk/core-paragraph/";
import Modal from "@vfuk/core-modal";
import Button from "@vfuk/core-button";
import Heading from "@vfuk/core-heading";
import LoginForm from "./forms/LoginForm";
import MFAForm from "./forms/MFAForm";
import SetupMFAForm from "./forms/SetupMFAForm";
import ChangePasswordForm from "./forms/ChangePasswordForm";
import AuthenticationService from "@services/AuthenticationService";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { OverlayProvider } from "@vfuk/core-overlay-controller";
import { setCookie, getHostName } from "@ucchub/ui";
import Spacing from "@vfuk/core-spacing";
import { useTranslation } from "react-i18next";
import {
  ReturnStep,
  ScrollableContainerStyles,
  SpinnerLoader,
} from "@ucchub/ui";
import getCookie from "@vfuk/core-helpers/dist/getCookie";
import ForgotPasswordForm from "./forms/ForgotPasswordForm";
import type { AxiosResponse, AxiosError } from "axios";
import ForgotPasswordSuccessState from "./states/ForgotPasswordSuccessState";
import AuthWarning from "./AuthWarning";
import { LOCAL_STORAGE_USER_SESSION_ID_KEY } from "@ucchub/ui/src/utils/createBaseService";

const COOKIE_EXPIRY_DAYS = 7;

const deleteAllLegacyAuthCookies = () => {
  // delete all Legacy Auth Cookies
  // this function can be removed after releasing this code on prod

  setCookie("Token", "", {
    expires: -1,
  });
  setCookie("refreshToken", "", {
    expires: -1,
  });
  setCookie("Header", "", {
    expires: -1,
  });
  sessionStorage.removeItem(LOCAL_STORAGE_USER_SESSION_ID_KEY);
};

export const AuthFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Login Form State
  const [loginState, setLoginState] = useState<
    | "INITIAL_LOGIN"
    | "MFA_REQUIRED"
    | "MFA_SETUP_REQUIRED"
    | "CHANGE_PASSWORD"
    | "FORGOT_PASSWORD"
  >("INITIAL_LOGIN");

  // Error or Loading States
  const [error, setError] = useState("");
  const [hasValidationError, setValidationError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isLoadingRegister, setLoadingRegister] = useState(false);

  // Authentication Value States
  const [email, setEmail] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [twoFactor, setTwoFactor] = useState("");
  const [QRCode, setQRCode] = useState("");

  // Screen States
  const [forgotPasswordSuccessMessage, setForgotPasswordSuccessMessage] =
    useState("");

  // Validation used for ChangePasswordForm
  const hasEnoughCharacters =
    newPassword.length > 9 && confirmNewPassword.length > 9;

  const hasLettersNumbersSymbols =
    /^(?=.*\d)(?=.*[a-z|A-Z]).{8,}$/.test(newPassword) &&
    /^(?=.*\d)(?=.*[a-z|A-Z]).{8,}$/.test(confirmNewPassword);

  const hasUpperAndLowerCase =
    /[a-z]/.test(newPassword) &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(confirmNewPassword) &&
    /[A-Z]/.test(confirmNewPassword);

  const { search } = useLocation();
  const [params] = useSearchParams();

  const hasSessionExpired = search === "?sessionExpired";
  const hasSessionExpiredFromSupportSite =
    search === "?sessionExpiredFromSupportSite";
  const registrationToken = params.get("registration_token") || undefined;
  const resetPasswordCode = params.get("reset") || undefined;

  // Used for fetching the registration token
  const fetchRegistrationToken = useCallback(async () => {
    if (!registrationToken) return;
    setLoadingRegister(true);
    await AuthenticationService.register(registrationToken)
      .then((res) => {
        // If the response is successful, we'll go onto change password
        if (res.data?.code === "CHANGE_PASSWORD")
          setLoginState("CHANGE_PASSWORD");

        // If the response is anything else, simply display the message.
        setError(res.data.message);
      })
      .catch((e) => setError(e.response.data.message));
    // Clear the loader
    setLoadingRegister(false);
  }, [registrationToken]);

  // If the registration or reset password token is present, fetch it on load
  useEffect(() => {
    if (registrationToken) fetchRegistrationToken();
    if (resetPasswordCode) setLoginState("CHANGE_PASSWORD");
  }, [fetchRegistrationToken, registrationToken, resetPasswordCode]);

  // If the user is already logged in, redirect to the dashboard.
  useEffect(() => {
    if (hasSessionExpiredFromSupportSite) {
      deleteAllLegacyAuthCookies();
      navigate("/login?sessionExpired");
      return;
    }

    const token = getCookie("Token");
    if (!token) return;
    window.location.href = "/";
  }, []);

  const onLogin = async () => {
    // Clear the "Session Expired" error if it's present
    if (hasSessionExpired) navigate("/login");

    // Set the initial states
    setLoading(true);
    setError("");

    // If there is a new password, make sure both match
    if (newPassword !== "") {
      if (newPassword !== confirmNewPassword) {
        setError(t("core:auth:password_mismatch") || "Passwords do not match");
        setLoading(false);
        return;
      }
    }

    let res: (AxiosResponse & AxiosError) | null = null;

    switch (loginState) {
      case "FORGOT_PASSWORD": {
        res = await AuthenticationService.forgotPassword(recoveryEmail).catch(
          (e) => setError(e.message)
        );
        break;
      }
      case "INITIAL_LOGIN":
        res = await AuthenticationService.login(email, password).catch((e) =>
          setError(e.message)
        );
        break;
      case "MFA_REQUIRED":
        res = await AuthenticationService.mfaRequired(
          email,
          password,
          twoFactor
        ).catch((e) => setError(e.message));
        break;
      case "MFA_SETUP_REQUIRED":
        res = await AuthenticationService.setupMfa(
          email,
          password,
          twoFactor,
          newPassword
        ).catch((e) => setError(e.message));
        break;
      case "CHANGE_PASSWORD":
        // If there is a reset password code, confirm the forgot password instead.
        if (resetPasswordCode) {
          res = await AuthenticationService.confirmForgotPassword(
            email,
            resetPasswordCode,
            newPassword
          ).catch((e) => setError(e.message));
        } else {
          res = await AuthenticationService.changePassword(
            email,
            password,
            newPassword,
            registrationToken
          ).catch((e) => setError(e.message));
        }
        break;
    }

    if (!res) {
      setError("We didn't get a response from the server.");
      setLoading(false);
      return;
    }

    if (res.status !== 200) {
      setError(`${res.response?.data.message}`);
      setLoading(false);
      return;
    }

    const resBody = await res.data;

    switch (resBody.code) {
      case "MFA_CODE_REQUIRED":
        setLoginState("MFA_REQUIRED");
        setLoading(false);
        break;
      case "SET_MFA":
        setLoginState("MFA_SETUP_REQUIRED");
        if (resBody.data.email) setEmail(resBody.data.email); // If the email is returned (if setting up via registration_token)
        setQRCode(
          `otpauth://totp/${email || resBody.data.email}?secret=${
            resBody.data.secretCode
          }`
        );
        setLoading(false);
        break;
      case "CHANGE_PASSWORD":
        setLoginState("CHANGE_PASSWORD");
        setLoading(false);
        break;
      case "AUTH_SUCCESS":
        setPassword("");
        setCookie("Token", res.headers.token, {
          expires: COOKIE_EXPIRY_DAYS,
          domain: getHostName(),
        });

        setCookie("refreshToken", res.headers.refreshtoken, {
          expires: COOKIE_EXPIRY_DAYS,
          domain: getHostName(),
        });

        setCookie("Header", res.headers["x-uc-hub-hdr"], {
          expires: COOKIE_EXPIRY_DAYS,
          domain: getHostName(),
        });

        sessionStorage.setItem(
          LOCAL_STORAGE_USER_SESSION_ID_KEY,
          `${res.headers.token}-${new Date().getTime()}`
        );

        window.location.href = "/";
        break;
      default:
        break;
    }

    // If the user has changed their password as a result of Forgot Password
    if (loginState === "CHANGE_PASSWORD" && resetPasswordCode) {
      setLoading(false);
      setForgotPasswordSuccessMessage(resBody.message);
    }

    // If we're calling this from the Forgot Password, screen, show the success screen.
    if (loginState === "FORGOT_PASSWORD") {
      setLoading(false);
      setForgotPasswordSuccessMessage(resBody.message);
    }

    // If there is an error
    if (resBody.error) {
      setLoading(false);
      setTwoFactor("");
      setConfirmNewPassword("");
      setError(resBody.error);
    }
  };

  const goBack = () => {
    setPassword("");
    setConfirmNewPassword("");
    setTwoFactor("");
    setError("");
    setQRCode("");
    setLoginState("INITIAL_LOGIN");
  };

  const isProceedDisabled = () => {
    if (hasValidationError) return true;

    switch (loginState) {
      case "INITIAL_LOGIN":
        return !email || !password;

      case "CHANGE_PASSWORD":
        // Disable submit if there is no password, or no confirm password,
        // if validation fails, if the passwords don't match
        // and if the email is not there if we're resetting password.
        return (
          !newPassword ||
          !confirmNewPassword ||
          !hasEnoughCharacters ||
          !hasLettersNumbersSymbols ||
          !hasUpperAndLowerCase ||
          newPassword !== confirmNewPassword ||
          (resetPasswordCode && !email)
        );

      case "MFA_REQUIRED":
        return !twoFactor;

      case "MFA_SETUP_REQUIRED":
        return !twoFactor;

      case "FORGOT_PASSWORD":
        return !recoveryEmail;

      default:
        return null;
    }
  };

  // If the registration is loading, show a loading screen
  if (isLoadingRegister)
    return (
      <SpinnerLoader
        srText={
          t("core:auth:loading_register") ||
          "Fetching your registration token..."
        }
        text={
          t("core:auth:loading_register") ||
          "Fetching your registration token..."
        }
      />
    );

  if (forgotPasswordSuccessMessage)
    return (
      <ForgotPasswordSuccessState
        // If they already have the code, display password change success screen
        // If they don't have the code, display email sent screen.
        title={
          resetPasswordCode
            ? "Password changed successfully!"
            : "Password reset email sent!"
        }
        // Display the dynamic message from the API.
        message={forgotPasswordSuccessMessage}
        onDone={() => {
          setLoginState("INITIAL_LOGIN");
          setForgotPasswordSuccessMessage("");
        }}
      />
    );

  const showLoginTitle =
    loginState !== "FORGOT_PASSWORD" && loginState !== "CHANGE_PASSWORD";

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      {showLoginTitle && <Heading size={3}>Login</Heading>}

      {loginState === "INITIAL_LOGIN" && (
        <Spacing spacingLevel={{ bottom: 6 }}>
          <AuthWarning />
        </Spacing>
      )}

      {loginState !== "INITIAL_LOGIN" && loginState !== "CHANGE_PASSWORD" && (
        <Spacing
          spacingLevel={{
            bottom: 6,
          }}
        >
          <ReturnStep displayText={email || "Back to Login"} onBack={goBack} />
        </Spacing>
      )}
      {loginState === "INITIAL_LOGIN" && hasSessionExpired && (
        <Spacing spacingLevel={{ bottom: 6 }}>
          <StateNotification state="warn">
            <Paragraph>{t("core:auth:session_expired")}</Paragraph>
          </StateNotification>
        </Spacing>
      )}
      {error && (
        <Spacing spacingLevel={{ bottom: 6 }}>
          <StateNotification state="error">
            <Paragraph>{error || "An unknown error occurred."}</Paragraph>
          </StateNotification>
        </Spacing>
      )}
      <div>
        {loginState === "FORGOT_PASSWORD" && (
          <ForgotPasswordForm
            email={recoveryEmail}
            setValidationError={setValidationError}
            setEmail={setRecoveryEmail}
          />
        )}
        {loginState === "INITIAL_LOGIN" && (
          <LoginForm
            onForgot={() => setLoginState("FORGOT_PASSWORD")}
            email={email}
            onEmail={setEmail}
            password={password}
            onPassword={setPassword}
          />
        )}
        {loginState === "MFA_REQUIRED" && (
          <MFAForm twoFactor={twoFactor} onTwoFactor={setTwoFactor} />
        )}
        {loginState === "MFA_SETUP_REQUIRED" && (
          <SetupMFAForm
            QRCodeValue={QRCode}
            twoFactor={twoFactor}
            onTwoFactor={setTwoFactor}
          />
        )}
        {loginState === "CHANGE_PASSWORD" && (
          <ChangePasswordForm
            newPassword={newPassword}
            onNewPassword={setNewPassword}
            confirmNewPassword={confirmNewPassword}
            onConfirmNewPassword={setConfirmNewPassword}
            hasLettersNumbersSymbols={hasLettersNumbersSymbols}
            hasEnoughCharacters={hasEnoughCharacters}
            hasUpperAndLowerCase={hasUpperAndLowerCase}
            email={email}
            onEmail={setEmail}
            resetPasswordCode={resetPasswordCode}
          />
        )}
      </div>
      <br />
      <Button
        text="Submit"
        htmlAttributes={{
          type: "submit",
        }}
        appearance="primary"
        width="full"
        onClick={onLogin}
        loading={isLoading}
        state={isProceedDisabled() || isLoading ? "disabled" : undefined}
      />
    </form>
  );
};

const AuthBox = ({ isOpen }: { isOpen: boolean }) => {
  const navigate = useNavigate();

  return (
    <OverlayProvider>
      <Modal
        srName="test-modal"
        isOpen={isOpen}
        size={4}
        side="left"
        type="flyout"
        animate
        isClosable
        onCloseCb={() => navigate("/")}
      >
        <ScrollableContainerStyles>
          <AuthFlow />
        </ScrollableContainerStyles>
      </Modal>
    </OverlayProvider>
  );
};

export default AuthBox;
